// Markdown — ChatGPT/Claude-style rich text renderer for ASSISTANT messages.
//
// Renders the assistant's reply as real markdown (headings, bold/italic,
// ordered + unordered lists, paragraphs, links, inline + fenced code,
// blockquotes, GFM tables, horizontal rules) instead of dumping the raw
// "**bold**" / "## heading" source into the bubble.
//
// SAFETY: rehype-raw is intentionally NOT enabled — any literal HTML in the
// model output is escaped and shown as text, never injected into the DOM.
// User messages stay plain pre-wrap; only assistant text flows through here.
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TEXT, TEXT2, TEXT3, FONT, MONO } from '../../shared';

const LINK = '#0284C7';
const CODE_BG = 'rgba(10,27,58,0.06)';
const CODE_BORDER = 'rgba(10,27,58,0.10)';

// Drop the trailing block margin so the bubble hugs the last line. Injected
// once per document (block-level children carry their own bottom margins).
if (typeof document !== 'undefined' && !document.getElementById('vella-markdown-style')) {
  const s = document.createElement('style');
  s.id = 'vella-markdown-style';
  s.textContent = '.vella-markdown > :last-child { margin-bottom: 0 !important; }';
  document.head.appendChild(s);
}

// Shared paragraph/line-height rhythm.
const BASE_LH = 1.6;

const COMPONENTS = {
  p: ({ node, ...props }) => (
    <p style={{ margin: '0 0 10px', lineHeight: BASE_LH, color: TEXT }} {...props} />
  ),
  h1: ({ node, ...props }) => (
    <h1 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', margin: '14px 0 8px', lineHeight: 1.25, color: TEXT }} {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.015em', margin: '14px 0 8px', lineHeight: 1.3, color: TEXT }} {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 style={{ fontSize: 15, fontWeight: 700, margin: '12px 0 6px', lineHeight: 1.35, color: TEXT }} {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 style={{ fontSize: 14, fontWeight: 700, margin: '10px 0 6px', lineHeight: 1.4, color: TEXT }} {...props} />
  ),
  h5: ({ node, ...props }) => (
    <h5 style={{ fontSize: 13, fontWeight: 700, margin: '10px 0 4px', color: TEXT }} {...props} />
  ),
  h6: ({ node, ...props }) => (
    <h6 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '10px 0 4px', color: TEXT3 }} {...props} />
  ),
  strong: ({ node, ...props }) => (
    <strong style={{ fontWeight: 700, color: TEXT }} {...props} />
  ),
  em: ({ node, ...props }) => <em style={{ fontStyle: 'italic' }} {...props} />,
  a: ({ node, ...props }) => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: LINK, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, wordBreak: 'break-word' }}
      {...props}
    />
  ),
  ul: ({ node, ...props }) => (
    <ul style={{ margin: '0 0 10px', paddingLeft: 22, lineHeight: BASE_LH, color: TEXT, listStyleType: 'disc' }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol style={{ margin: '0 0 10px', paddingLeft: 22, lineHeight: BASE_LH, color: TEXT }} {...props} />
  ),
  li: ({ node, ...props }) => <li style={{ margin: '3px 0' }} {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      style={{
        margin: '0 0 10px',
        padding: '4px 14px',
        borderLeft: '3px solid rgba(14,165,233,0.45)',
        color: TEXT2,
        fontStyle: 'italic',
        background: 'rgba(14,165,233,0.05)',
        borderRadius: '0 8px 8px 0',
      }}
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <hr style={{ border: 'none', borderTop: '1px solid rgba(10,27,58,0.12)', margin: '14px 0' }} {...props} />
  ),
  code: ({ node, className, children, ...props }) => {
    // react-markdown v9+ dropped the `inline` prop — infer block vs inline
    // from a language class or a multi-line body. Fenced blocks render inside
    // the <pre> styler below; everything else gets the inline "pill".
    const text = String(children ?? '');
    const isBlock = /language-/.test(className || '') || text.includes('\n');
    if (isBlock) {
      return (
        <code className={className} style={{ fontFamily: MONO, fontSize: 12.5, lineHeight: 1.55, color: TEXT }} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        style={{
          fontFamily: MONO,
          fontSize: '0.88em',
          background: CODE_BG,
          border: `1px solid ${CODE_BORDER}`,
          borderRadius: 5,
          padding: '1px 5px',
          color: TEXT,
          wordBreak: 'break-word',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ node, ...props }) => (
    <pre
      style={{
        margin: '0 0 10px',
        padding: '12px 14px',
        background: CODE_BG,
        border: `1px solid ${CODE_BORDER}`,
        borderRadius: 10,
        overflowX: 'auto',
        fontFamily: MONO,
      }}
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <div style={{ width: '100%', overflowX: 'auto', margin: '0 0 10px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }} {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead {...props} />,
  th: ({ node, ...props }) => (
    <th
      style={{
        textAlign: 'left',
        fontWeight: 700,
        color: TEXT,
        padding: '7px 10px',
        borderBottom: '2px solid rgba(10,27,58,0.15)',
        background: 'rgba(10,27,58,0.04)',
      }}
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td style={{ padding: '7px 10px', borderBottom: '1px solid rgba(10,27,58,0.08)', color: TEXT2 }} {...props} />
  ),
};

export default function Markdown({ children }) {
  return (
    <div
      className="vella-markdown"
      style={{ fontFamily: FONT, fontSize: 14, color: TEXT, wordBreak: 'break-word' }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {typeof children === 'string' ? children : ''}
      </ReactMarkdown>
    </div>
  );
}
